/**
 * Kurumsal bütçe/politika dışı rezervasyonlar için "Onaya Gönder" akışının ürün-nötr
 * çekirdeği — legacy `*Controller.SendForApproval` deseni. Bir seçim bütçeyi aşıyorsa
 * (`isOutBudget`) ve sepette bir `EmployeeID` (ya da oturumdaki personel) varsa, yolcu
 * bilgileri `CorporateEmployee/GetCorporateEmployeeById` ile personel kaydından
 * **salt-okunur** getirilir; "Rezervasyon" butonu "Onaya Gönder"e döner ve gönderim
 * normal `UpdatePassengers → Billing/Submit → (MakePrebooking)` zincirini çalıştırıp
 * (yöneticinin bekleyen listesine düşer) durur.
 *
 * Uçak modülü (`modules/air/corporateApproval.ts`) bunu `AirFlowState` üzerinden saran
 * ince bir adaptörle kullanır; otel/otobüs/transfer/araç kiralama FlowPage'leri doğrudan
 * `ApprovalGateInput` kurup çağırır.
 */
import type { Contact, Passenger } from '../../api/types/common';
import type {
  CorporateEmployee,
  CorporateIDCard,
  CorporatePassportDocument,
} from '../../api/types/corporateEmployee';
import { normalizePhoneToE164 } from '../../config/phoneRegions';

/** Her ürünün sepet şeklinin bu akışın okuduğu yapısal alt kümesi — `ShoppingCart`,
 *  `TransferShoppingCart`, `BusShoppingCart` hepsi bunu yapısal olarak karşılar. */
export interface ApprovableCartLike {
  employeeID?: number;
  isOutBudget?: boolean;
  policySendApproval?: boolean;
  maxBudget?: number;
  approvalStatus?: string;
}

/** Onay kapısının girdisi — allocate sonrası sepet + (varsa) seçili ücret/teklif/araç
 *  bütçe bayrağı + arama formundaki personel (sepet `EmployeeID` yankılamazsa yedek). */
export interface ApprovalGateInput {
  cart?: ApprovableCartLike | null;
  /** Seçili ücret/teklif/araç `isOutBudget` (sepet henüz taşımıyorsa). */
  extraOverBudget?: boolean;
  /** Seçili ücret/teklif/araç `maxBudget` (bilgi metni için). */
  extraMaxBudget?: number | null;
  /** Arama formunda seçilen personelin `employeeID`'si — allocate sepeti `EmployeeID`
   *  döndürmezse buradan gelir. */
  searchEmployeeId?: number | null;
}

/** Seçim bütçe/politika dışı mı — sepetten (Allocate yanıtı `ShoppingCart.IsOutBudget`
 *  / `PolicySendApproval` / `ApprovalStatus` taşıyor) ya da seçili üründen. */
export function isOverBudget(input: ApprovalGateInput): boolean {
  const cart = input.cart;
  return (
    cart?.isOutBudget === true ||
    cart?.policySendApproval === true ||
    cart?.approvalStatus === 'Pending' ||
    input.extraOverBudget === true
  );
}

/** Yolcu adımının "Onaya Gönder" (salt-okunur personel bilgisi) modunda olup olmadığı:
 *  bir personel var (sepette `EmployeeID` ya da arama formunda seçili) **ve** seçim
 *  bütçe/politika dışı. */
export function needsCorporateApproval(input: ApprovalGateInput): boolean {
  const hasEmployee = !!input.cart?.employeeID || !!input.searchEmployeeId;
  return hasEmployee && isOverBudget(input);
}

/** Yolcu bilgisini hangi personelden çekeceğiz: sepetteki `EmployeeID`, yoksa arama
 *  formundaki personel, yoksa oturum sahibinin personel kimliği. */
export function approvalEmployeeId(
  input: ApprovalGateInput,
  sessionEmployeeId?: number | null
): number | undefined {
  return input.cart?.employeeID || input.searchEmployeeId || sessionEmployeeId || undefined;
}

/** İzin verilen kurumsal bütçe (bilgi metni için). */
export function corporateMaxBudget(input: ApprovalGateInput): number | null {
  const budget = input.cart?.maxBudget ?? input.extraMaxBudget ?? undefined;
  return typeof budget === 'number' && budget > 0 ? budget : null;
}

/** ISO / `YYYY-MM-DD...` → `YYYY-MM-DD`. Geçersizse `''`. */
export function toIsoDateOnly(value: string | undefined | null): string {
  if (!value) return '';
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
  if (m) return m[1];
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

const genderFromEmployee = (g: string | undefined): string => {
  const t = (g ?? '').toLocaleLowerCase('tr');
  if (t.startsWith('erkek') || t === 'male' || t === 'm' || t === 'bay') return 'Male';
  if (t.startsWith('kad') || t.startsWith('bayan') || t === 'female' || t === 'f') return 'Female';
  return g ?? '';
};

/** Personel kaydı (+ varsa kimlik kartı / pasaport) → tek `Passenger`. Yurtdışı seçimde
 *  `citizenNo` sentinel `"00000000000"` (uçuş akışıyla aynı). */
export function employeeToPassenger(
  employee: CorporateEmployee,
  opts: { idCard?: CorporateIDCard; passport?: CorporatePassportDocument; isInternational: boolean }
): Passenger {
  const citizenNo = opts.isInternational ? '00000000000' : opts.idCard?.cardNumber ?? '';
  return {
    paxType: 'ADT',
    firstName: employee.name,
    lastName: employee.surName,
    gender: genderFromEmployee(employee.gender),
    birthDate: toIsoDateOnly(employee.dateOfBirth),
    email: employee.emailAdress,
    phone: normalizePhoneToE164(employee.phoneNumber),
    citizenNo,
    nationality: opts.idCard?.country || 'TR',
    passportNo: opts.passport?.passportNumber ?? '',
    passportExpireDate: toIsoDateOnly(opts.passport?.expirationDate),
    frequentFlayerNo: '',
    isChild: false,
  };
}

export function employeeToContact(employee: CorporateEmployee): Contact {
  return {
    name: employee.name,
    lastName: employee.surName,
    email: employee.emailAdress,
    phone: normalizePhoneToE164(employee.phoneNumber),
  };
}
